"use client";

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	AlertTriangle,
	Building2,
	CalendarClock,
	ChevronDown,
	Info,
	Loader2,
	MapPin,
	Plus,
	User,
	X,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { fetchContactPeople } from '@/lib/contacts/fetchContactPeople';
import LocationAutocomplete, { CustomLocationResult } from '@/components/maps/LocationAutocomplete';
import type { CustomLocationData } from '@/components/maps/LocationAutocomplete';
import AppointmentScheduler, { getDefaultAppointmentSchedule } from '@/components/work-items/AppointmentScheduler';
import UnifiedContactLookup, {
	type UnifiedLookupContact,
	type UnifiedLookupContactPerson,
	type UnifiedLookupLocation,
	type UnifiedLookupSearchResult,
} from '@/components/shared/UnifiedContactLookup';

import PurchaseItemList, { type PurchaseItemListHandle } from '@/components/work-items/PurchaseItemList';
import { normalizePurchaseListItems, type PurchaseListItem } from '@/components/work-items/purchaseListUtils';
import { buildMissingCoordinatesMessage, extractCoordinates, formatLocationAddress, getContactLocationCoordinates } from '@/lib/work-items/appointmentLocationUtils';
import { findAppointmentClashes, isScheduleHiddenAppointmentStatus } from '@/lib/work-items/appointmentClashDetection';
import { formatDateTimeSAST, formatLocalISOSAST, formatTimeSAST, getTimePartsSAST, SAST_TIMEZONE } from '@/lib/sastTime';
import { normalizeAppointmentTypeKey, PURCHASE_APPOINTMENT_TYPE_KEY } from '@/lib/work-items/appointmentTypeConfig';
import { formatLocalDateTimeValue, getAppointmentScheduleErrorMessage, getAppointmentScheduleValidation } from '@/lib/work-items/appointmentSchedule';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { useTheme } from '@/contexts/ThemeContext';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { MOBILE_SAFE_AREA_BOTTOM, MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

const APPOINTMENT_CREATE_LOADING_OVERLAY_Z = 10040;

type AppointmentTypeOption = {
	key: string;
	label: string;
	visible_to_customer?: boolean;
};

const APPOINTMENT_PRIORITIES = [
	{ value: 'low', label: 'Low', icon: '○' },
	{ value: 'medium', label: 'Medium', icon: '●' },
	{ value: 'high', label: 'High', icon: '▲' },
	{ value: 'urgent', label: 'Urgent', icon: '🔴' },
];

type ContactLocation = {
	id: number;
	location_name?: string | null;
	address_line1?: string | null;
	address_line2?: string | null;
	city?: string | null;
	province?: string | null;
	postal_code?: string | null;
	country?: string | null;
	latitude?: number | string | null;
	longitude?: number | string | null;
	coordinates?: unknown;
};

type ContactOption = {
	id: number;
	first_name?: string | null;
	last_name?: string | null;
	name?: string | null;
	company_name?: string | null;
	contact_person?: string | null;
	email?: string | null;
	phone?: string | null;
	account_status?: string | null;
	is_active?: boolean | null;
	payment_terms?: string | number | null;
	payment_terms_days?: string | number | null;
	credit_limit?: number | string | null;
	current_balance?: number | string | null;
	currency?: string | null;
};

type ContactPersonOption = {
	id: number;
	first_name?: string | null;
	last_name?: string | null;
	name?: string | null;
	email?: string | null;
	phone?: string | null;
	position?: string | null;
};

type AssigneeOption = {
	id: number;
	first_name?: string | null;
	last_name?: string | null;
	email?: string | null;
	name?: string | null;
	assignee_type?: 'user' | 'contractor';
};

type ExistingAppointment = {
	id: string;
	title: string;
	scheduled_start: string;
	scheduled_end: string;
	status: string;
	item_number: string;
};

export interface AppointmentDropSetupSelection {
	appointmentTitle: string;
	appointmentType: string;
	appointmentPriority: string;
	scheduledStartISO: string;
	scheduledEndISO: string;
	purchaseItems: PurchaseListItem[];
	additionalNotes: string;
	equipmentRequired: string;
	safetyRequirements: string;
	resolvedContactId: number | null;
	resolvedContactName?: string | null;
	resolvedContactEmail?: string | null;
	resolvedContactPhone?: string | null;
	resolvedLocationId: number | null;
	resolvedLocationName?: string | null;
	resolvedLocationAddress?: string | null;
	resolvedCoordinates?: { lat: number; lng: number } | null;
	resolvedContactPersonId: number | null;
	resolvedContactPerson?: string | null;
	resolvedContactPersonEmail?: string | null;
	resolvedContactPersonPhone?: string | null;
}

interface AppointmentDropSetupModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (selection: AppointmentDropSetupSelection) => Promise<void> | void;
	job: WorkItem | null;
	scheduledStartISO?: string | null;
	scheduledEndISO?: string | null;
	assignedUserIds?: Array<number | string | null | undefined>;
	availableAssignees?: AssigneeOption[];
	appointmentTypeOptions: AppointmentTypeOption[];
	/** Resolved `appointment.create` action permission for the job's workflow
	 *  slice. When false the Create button is disabled and confirm is rejected. */
	canCreate?: boolean;
}

type LocationSource = 'contact' | 'different_contact' | 'custom';

const DEFAULT_APPOINTMENT_TYPE = 'site_visit';
const TIMELINE_START_HOUR = 6;
const TIMELINE_END_HOUR = 20;
const TIMELINE_HOURS = TIMELINE_END_HOUR - TIMELINE_START_HOUR;
const EMPTY_ASSIGNED_USER_IDS: Array<number | string | null | undefined> = [];

const getInitialLocalScheduleValue = (value?: string | null, fallback?: string) => {
	if (!value) {
		return fallback || '';
	}

	const parsedValue = new Date(value);
	if (Number.isNaN(parsedValue.getTime())) {
		return fallback || '';
	}

	return formatLocalDateTimeValue(parsedValue);
};

const buildName = (value?: string | null, first?: string | null, last?: string | null) => {
	if (value && value.trim()) return value.trim();
	const combined = `${first || ''} ${last || ''}`.trim();
	return combined || '';
};

const buildContactDisplayName = (contact?: ContactOption | null) => {
	if (!contact) return '';
	return (
		contact.company_name?.trim() ||
		buildName(contact.name, contact.first_name, contact.last_name) ||
		contact.contact_person?.trim() ||
		''
	);
};

const normalizeLocationId = (value: number | string | null | undefined): number | null => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (typeof value === 'string') {
		const trimmedValue = value.trim();
		if (!trimmedValue) {
			return null;
		}

		const parsedValue = Number.parseInt(trimmedValue, 10);
		return Number.isFinite(parsedValue) ? parsedValue : null;
	}

	return null;
};

const normalizeUserId = (value: number | string | null | undefined): number | null => {
	if (typeof value === 'number') {
		return Number.isFinite(value) && value > 0 ? value : null;
	}

	if (typeof value === 'string') {
		const trimmedValue = value.trim();
		if (!trimmedValue) {
			return null;
		}

		const parsedValue = Number.parseInt(trimmedValue, 10);
		return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
	}

	return null;
};

const buildAssigneeDisplayName = (assignee?: AssigneeOption | null) => {
	if (!assignee) return '';

	const fullName = [assignee.first_name, assignee.last_name]
		.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
		.join(' ')
		.trim();

	return assignee.name?.trim() || fullName || assignee.email || '';
};

function DayScheduleTimeline({
	memberName,
	appointments,
	proposedStart,
	proposedEnd,
	isDark,
}: {
	memberName: string;
	appointments: ExistingAppointment[];
	proposedStart: string;
	proposedEnd: string;
	isDark: boolean;
}) {
	const toMinutesFromStart = (dateStr: string) => {
		const { hours, minutes } = getTimePartsSAST(dateStr);
		const totalMinutes = hours * 60 + minutes;
		return Math.max(0, Math.min(totalMinutes - TIMELINE_START_HOUR * 60, TIMELINE_HOURS * 60));
	};

	const pct = (minutes: number) => (minutes / (TIMELINE_HOURS * 60)) * 100;
	const sortedAppointments = [...appointments].sort(
		(a, b) => new Date(a.scheduled_start).getTime() - new Date(b.scheduled_start).getTime()
	);
	const hasProposedSlot = Boolean(proposedStart && proposedEnd);
	const proposedLeftPct = hasProposedSlot ? pct(toMinutesFromStart(proposedStart)) : 0;
	const proposedWidthPct = hasProposedSlot
		? pct(toMinutesFromStart(proposedEnd) - toMinutesFromStart(proposedStart))
		: 0;
	const hasConflict = hasProposedSlot && findAppointmentClashes(sortedAppointments, {
		newStart: new Date(proposedStart),
		newEnd: new Date(proposedEnd),
		excludeHiddenStatuses: true,
	}).length > 0;

	const hourMarkers = [] as number[];
	for (let hour = TIMELINE_START_HOUR; hour <= TIMELINE_END_HOUR; hour += 2) {
		hourMarkers.push(hour);
	}

	return (
		<div className="mb-3 last:mb-0">
			<div className={`text-xs font-medium mb-1 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
				<span>{memberName}</span>
				{hasConflict ? (
					<span className="flex items-center gap-1 text-red-500">
						<AlertTriangle className="w-3 h-3" />
						<span className="font-semibold">Conflict</span>
					</span>
				) : sortedAppointments.length === 0 ? (
					<span className={`flex items-center gap-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
						<CalendarClock className="w-3 h-3" />
						<span>Free</span>
					</span>
				) : null}
			</div>

			<div className={`relative h-10 rounded-lg overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
				{hourMarkers.map((hour) => (
					<div
						key={hour}
						className={`absolute top-0 bottom-0 border-l ${isDark ? 'border-gray-600' : 'border-gray-200'}`}
						style={{ left: `${pct((hour - TIMELINE_START_HOUR) * 60)}%` }}
					/>
				))}

				{sortedAppointments.map((appointment) => {
					const left = pct(toMinutesFromStart(appointment.scheduled_start));
					const width = Math.max(
						pct(toMinutesFromStart(appointment.scheduled_end) - toMinutesFromStart(appointment.scheduled_start)),
						1
					);

					return (
						<div
							key={appointment.id}
							className={`absolute top-1 bottom-1 rounded-md flex items-center justify-center overflow-hidden cursor-default group ${
								isDark ? 'bg-amber-700/70 border border-amber-600' : 'bg-amber-200 border border-amber-300'
							}`}
							style={{ left: `${left}%`, width: `${width}%` }}
							title={`${appointment.item_number}: ${appointment.title}\n${formatTimeSAST(appointment.scheduled_start)} – ${formatTimeSAST(appointment.scheduled_end)}`}
						>
							<span className={`text-[10px] font-medium truncate px-1 ${isDark ? 'text-amber-100' : 'text-amber-900'}`}>
								{formatTimeSAST(appointment.scheduled_start)}-{formatTimeSAST(appointment.scheduled_end)}
							</span>
						</div>
					);
				})}

				{hasProposedSlot && proposedWidthPct > 0 ? (
					<div
						className={`absolute top-0 bottom-0 rounded-md border-2 border-dashed ${
							hasConflict
								? 'bg-red-500/20 border-red-500'
								: isDark ? 'bg-green-500/25 border-green-400' : 'bg-green-500/20 border-green-500'
						}`}
						style={{ left: `${proposedLeftPct}%`, width: `${proposedWidthPct}%` }}
						title={`New: ${formatTimeSAST(proposedStart)} – ${formatTimeSAST(proposedEnd)}`}
					>
						<span className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold ${
							hasConflict ? 'text-red-600' : isDark ? 'text-green-300' : 'text-green-700'
						}`}>
							NEW
						</span>
					</div>
				) : null}
			</div>

			<div className="relative h-4 mt-0.5">
				{hourMarkers.map((hour) => (
					<span
						key={hour}
						className={`absolute text-[10px] -translate-x-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
						style={{ left: `${pct((hour - TIMELINE_START_HOUR) * 60)}%` }}
					>
						{String(hour).padStart(2, '0')}:00
					</span>
				))}
			</div>

			{sortedAppointments.length > 0 ? (
				<div className="mt-1 space-y-0.5">
					{sortedAppointments.map((appointment) => (
						<div key={appointment.id} className={`text-[11px] flex items-center gap-1.5 px-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
							<span className={`font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
								{formatTimeSAST(appointment.scheduled_start)}–{formatTimeSAST(appointment.scheduled_end)}
							</span>
							<span className="truncate">{appointment.item_number}: {appointment.title}</span>
							<span className={`text-[10px] px-1 rounded ${
								appointment.status === 'in_progress'
									? (isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700')
									: appointment.status === 'completed'
										? (isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700')
										: isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
							}`}>
								{appointment.status}
							</span>
						</div>
					))}
				</div>
			) : null}
		</div>
	);
}

const findPreferredContactLocation = (
	locations: ContactLocation[],
	selectedLocationId: number | string | null | undefined,
	fallbackLocationId?: number | string | null,
) => {
	const normalizedSelectedLocationId = normalizeLocationId(selectedLocationId);
	if (normalizedSelectedLocationId != null) {
		const selectedLocation = locations.find((location) => normalizeLocationId(location.id) === normalizedSelectedLocationId);
		if (selectedLocation) {
			return selectedLocation;
		}
	}

	const normalizedFallbackLocationId = normalizeLocationId(fallbackLocationId);
	if (normalizedFallbackLocationId != null) {
		const fallbackLocation = locations.find((location) => normalizeLocationId(location.id) === normalizedFallbackLocationId);
		if (fallbackLocation) {
			return fallbackLocation;
		}
	}

	return locations.find((location) => !!getContactLocationCoordinates(location)) || locations[0] || null;
};

const normalizeContactLocationRecord = (location: ContactLocation): ContactLocation => {
	const coordinates = getContactLocationCoordinates(location);

	if (!coordinates) {
		return location;
	}

	return {
		...location,
		latitude: location.latitude ?? coordinates.lat,
		longitude: location.longitude ?? coordinates.lng,
		coordinates: location.coordinates ?? { lat: coordinates.lat, lng: coordinates.lng },
	};
};

const buildJobCustomLocationState = (job: WorkItem | null) => {
	if (!job) {
		return {
			searchValue: '',
			selectedLocation: null as CustomLocationData | null,
		};
	}

	const name = job.location_name?.trim() || '';
	const address = job.location_address?.trim() || '';
	const searchValue = name || address;

	if (!searchValue) {
		return {
			searchValue: '',
			selectedLocation: null as CustomLocationData | null,
		};
	}

	const coordinates =
		extractCoordinates(job.location_coordinates);

	if (!coordinates) {
		return {
			searchValue: '',
			selectedLocation: null as CustomLocationData | null,
		};
	}

	return {
		searchValue: name || address,
		selectedLocation: {
			name: name || address,
			address,
			lat: String(coordinates.lat),
			lng: String(coordinates.lng),
		},
	};
};

function OptionalAccordionSection({
	title,
	description,
	icon,
	isOpen,
	onToggle,
	isDark,
	children,
}: {
	title: string;
	description: string;
	icon: React.ReactNode;
	isOpen: boolean;
	onToggle: () => void;
	isDark: boolean;
	children: React.ReactNode;
}) {
	return (
		<div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/80'}`}>
			<button
				type="button"
				onClick={onToggle}
				className={`flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors ${
					isDark ? 'hover:bg-gray-800/70' : 'hover:bg-white/80'
				}`}
				aria-expanded={isOpen}
			>
				<div className="flex min-w-0 items-start gap-3">
					<div className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 shadow-sm'}`}>
						{icon}
					</div>
					<div className="min-w-0">
						<h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{title}</h3>
						<p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{description}</p>
					</div>
				</div>
				<ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
			</button>

			{isOpen ? (
				<div className={`border-t px-4 pb-4 pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
					{children}
				</div>
			) : null}
		</div>
	);
}

const AppointmentDropSetupModal: React.FC<AppointmentDropSetupModalProps> = ({
	isOpen,
	onClose,
	onConfirm,
	job,
	scheduledStartISO,
	scheduledEndISO,
	assignedUserIds = EMPTY_ASSIGNED_USER_IDS,
	availableAssignees = [],
	appointmentTypeOptions,
	canCreate = true,
}) => {
	const [appointmentType, setAppointmentType] = useState('');
	const [appointmentPriority, setAppointmentPriority] = useState('');
	const [appointmentTitle, setAppointmentTitle] = useState('');
	const [locationSource, setLocationSource] = useState<LocationSource>('contact');
	const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
	const [selectedContactPersonId, setSelectedContactPersonId] = useState<number | null>(null);
	const [overrideContact, setOverrideContact] = useState<ContactOption | null>(null);
	const [overrideLocationId, setOverrideLocationId] = useState<number | null>(null);
	const [customContactPerson, setCustomContactPerson] = useState('');
	const [customLocation, setCustomLocation] = useState<CustomLocationData | null>(null);
	const [customLocationSearch, setCustomLocationSearch] = useState('');
	const [purchaseItems, setPurchaseItems] = useState<PurchaseListItem[]>([]);
	const [additionalNotes, setAdditionalNotes] = useState('');
	const [equipmentRequired, setEquipmentRequired] = useState('');
	const [safetyRequirements, setSafetyRequirements] = useState('');
	const [scheduledStart, setScheduledStart] = useState('');
	const [scheduledEnd, setScheduledEnd] = useState('');
	const [availability, setAvailability] = useState<Record<number, ExistingAppointment[]>>({});
	const [contacts, setContacts] = useState<ContactOption[]>([]);
	const [contactLocations, setContactLocations] = useState<ContactLocation[]>([]);
	const [contactPeople, setContactPeople] = useState<ContactPersonOption[]>([]);
	const [overrideLocations, setOverrideLocations] = useState<ContactLocation[]>([]);
	const [overrideContactPeople, setOverrideContactPeople] = useState<ContactPersonOption[]>([]);
	const [loadingContacts, setLoadingContacts] = useState(false);
	const [loadingContactLocations, setLoadingContactLocations] = useState(false);
	const [loadingContactPeople, setLoadingContactPeople] = useState(false);
	const [loadingOverrideLocations, setLoadingOverrideLocations] = useState(false);
	const [loadingOverrideContactPeople, setLoadingOverrideContactPeople] = useState(false);
	const [loadingAvailability, setLoadingAvailability] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [expandedRequirements, setExpandedRequirements] = useState(false);
	const purchaseItemListRef = useRef<PurchaseItemListHandle | null>(null);
	const latestInheritedContactDataRequestId = useRef(0);
	const latestOverrideLocationsLoadRequestId = useRef(0);
	const latestOverridePeopleLoadRequestId = useRef(0);
	const activeOverrideContactIdRef = useRef<number | null>(null);
	const inheritedContactDataCacheRef = useRef(new Map<number, { locations: ContactLocation[]; people: ContactPersonOption[] }>());
	const overrideContactDataCacheRef = useRef(new Map<number, { locations: ContactLocation[]; people: ContactPersonOption[] }>());
	const { currentTheme } = useTheme();
	const isDark = currentTheme?.mode === 'dark';

	const labelClassName = `block text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`;
	const subtleTextClassName = `text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
	const bodyTextClassName = `text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
	const sectionCardClassName = `rounded-lg border p-4 ${
		isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
	}`;
	const detailPanelClassName = `rounded-lg border p-4 ${
		isDark ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-white'
	}`;
	const fieldClassName = `mt-2 w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 ${
		isDark
			? 'border-gray-600 bg-gray-700 text-gray-100 placeholder-gray-400 focus:border-green-500 focus:ring-green-500/30'
			: 'border-gray-300 bg-white text-gray-900 focus:border-green-500 focus:ring-green-200'
	}`;

	const inheritedContactPersonName = useMemo(() => {
		if (!job) return '';
		return job.contact_person || '';
	}, [job]);

	const normalizedJobLocationId = useMemo(
		() => normalizeLocationId(job?.location_id),
		[job?.location_id]
	);

	const normalizedJobContactId = useMemo(
		() => normalizeLocationId(job?.contact_id),
		[job?.contact_id]
	);

	const inheritedPurchaseItems = useMemo(
		() => normalizePurchaseListItems((job as any)?.custom_fields?.purchase_items),
		[job]
	);

	const jobResetKey = useMemo(() => {
		if (!job) return 'closed';

		return JSON.stringify({
			id: job.id ?? null,
			contactId: normalizeLocationId(job.contact_id),
			contactPersonId: normalizeLocationId(job.contact_person_id),
			contactPerson: job.contact_person ?? '',
			locationId: normalizeLocationId(job.location_id),
			locationName: job.location_name ?? '',
			locationAddress: job.location_address ?? '',
			title: job.title ?? '',
			subject: job.subject ?? '',
			itemNumber: job.item_number ?? '',
			purchaseItems: (job as any)?.custom_fields?.purchase_items ?? null,
			additionalNotes: (job as any)?.notes ?? (job as any)?.custom_fields?.additional_notes ?? '',
			equipmentRequired: (job as any)?.custom_fields?.equipment_required ?? '',
			safetyRequirements: (job as any)?.custom_fields?.safety_requirements ?? '',
			scheduledStartISO: scheduledStartISO ?? '',
			scheduledEndISO: scheduledEndISO ?? '',
		});
	}, [job, scheduledEndISO, scheduledStartISO]);

	const resetSnapshot = useMemo(() => {
		if (!job) {
			return null;
		}

		const initialCustomLocation = buildJobCustomLocationState(job);
		const defaultSchedule = getDefaultAppointmentSchedule();
		const nextScheduledStart = getInitialLocalScheduleValue(scheduledStartISO, defaultSchedule.scheduled_start);
		const nextScheduledEnd = getInitialLocalScheduleValue(scheduledEndISO, defaultSchedule.scheduled_end);

		return {
			key: jobResetKey,
			appointmentTitle: job.subject || job.title || `Appointment for ${job.item_number || job.id}`,
			selectedLocationId: job.location_id ?? null,
			selectedContactPersonId: job.contact_person_id ?? null,
			customContactPerson: job.contact_person || '',
			customLocation: initialCustomLocation.selectedLocation,
			customLocationSearch: initialCustomLocation.searchValue,
			purchaseItems: normalizePurchaseListItems((job as any).custom_fields?.purchase_items),
			additionalNotes: (job as any).notes || (job as any).custom_fields?.additional_notes || '',
			equipmentRequired: (job as any).custom_fields?.equipment_required || '',
			safetyRequirements: (job as any).custom_fields?.safety_requirements || '',
			scheduledStart: nextScheduledStart,
			scheduledEnd: nextScheduledEnd,
			locationSource: job.location_id
				? 'contact'
				: job.location_name || job.location_address
					? 'custom'
					: job.contact_id
						? 'contact'
						: 'custom' as LocationSource,
		};
	}, [job, jobResetKey, scheduledEndISO, scheduledStartISO]);

	const selectedContact = useMemo<ContactOption | null>(() => {
		if (!job) return null;

		if (job.contact_id == null && !job.contact_name) {
			return null;
		}

		return {
			id: job.contact_id ?? -1,
			company_name: job.contact_name || null,
			contact_person: job.contact_name || null,
			email: (job as any).contact_email || null,
			phone: (job as any).contact_phone || null,
		};
	}, [job]);

	const activeSelectedContact = useMemo<ContactOption | null>(
		() => (locationSource === 'different_contact' ? overrideContact : selectedContact),
		[locationSource, overrideContact, selectedContact]
	);

	const inheritedLocation = useMemo(
		() => findPreferredContactLocation(contactLocations, selectedLocationId, normalizedJobLocationId),
		[contactLocations, normalizedJobLocationId, selectedLocationId]
	);

	const resolvedDifferentLocation = useMemo(
		() => overrideLocations.find((location) => location.id === overrideLocationId) || null,
		[overrideLocationId, overrideLocations]
	);

	const inheritedContactPerson = useMemo(
		() => contactPeople.find((person) => person.id === selectedContactPersonId) || null,
		[contactPeople, selectedContactPersonId]
	);

	const resolvedDifferentContactPerson = useMemo(
		() => overrideContactPeople.find((person) => person.id === selectedContactPersonId) || null,
		[overrideContactPeople, selectedContactPersonId]
	);

	const selectedLocation = useMemo(() => {
		if (locationSource === 'different_contact') {
			return resolvedDifferentLocation;
		}

		return inheritedLocation;
	}, [inheritedLocation, locationSource, resolvedDifferentLocation]);

	const selectedPerson = useMemo(
		() => (locationSource === 'different_contact' ? resolvedDifferentContactPerson : inheritedContactPerson),
		[inheritedContactPerson, locationSource, resolvedDifferentContactPerson]
	);

	const sharedContacts = useMemo<UnifiedLookupContact[]>(() => (
		contacts.map((contact) => ({
			id: contact.id,
			company_name: contact.company_name ?? null,
			contact_person: buildName(contact.name, contact.first_name, contact.last_name) || contact.contact_person || null,
			email: contact.email ?? null,
			phone: contact.phone ?? null,
			contact_type: 'customer',
			account_status: contact.account_status ?? null,
			is_active: contact.is_active ?? null,
			payment_terms: contact.payment_terms ?? null,
			payment_terms_days: contact.payment_terms_days ?? null,
			credit_limit: contact.credit_limit ?? null,
			current_balance: contact.current_balance ?? null,
			currency: contact.currency ?? null,
		}))
	), [contacts]);

	const sharedOverrideLocations = useMemo<UnifiedLookupLocation[]>(() => (
		overrideLocations.map((location) => ({
			id: location.id,
			contact_id: overrideContact?.id ?? 0,
			location_name: location.location_name || `Location #${location.id}`,
			address_line1: location.address_line1 ?? null,
			address_line2: location.address_line2 ?? null,
			city: location.city ?? null,
			state: location.province ?? null,
			postal_code: location.postal_code ?? null,
			country: location.country ?? null,
			contact_person: null,
			contact_email: null,
			contact_phone: null,
			is_primary: false,
		}))
	), [overrideContact?.id, overrideLocations]);

	const sharedOverridePeople = useMemo<UnifiedLookupContactPerson[]>(() => (
		overrideContactPeople.map((person) => ({
			id: person.id,
			contact_id: overrideContact?.id ?? 0,
			first_name: person.first_name || '',
			last_name: person.last_name || '',
			job_title: person.position ?? null,
			email: person.email ?? null,
			phone: person.phone ?? null,
			mobile: null,
			is_primary: false,
			default_location_id: null,
			default_location_name: null,
			contact_company_name: buildContactDisplayName(overrideContact) || null,
		}))
	), [overrideContact, overrideContactPeople]);

	const hasCustomLoc = locationSource === 'custom' && !!customLocation;
	const hasContactLoc = (locationSource === 'contact' || locationSource === 'different_contact') && !!selectedLocation;
	const hasValidLocation = hasCustomLoc || hasContactLoc;

	const isPurchaseAppointmentType = useMemo(
		() => normalizeAppointmentTypeKey(appointmentType) === PURCHASE_APPOINTMENT_TYPE_KEY,
		[appointmentType]
	);

	const normalizedPurchaseItems = useMemo(
		() => normalizePurchaseListItems(purchaseItems),
		[purchaseItems]
	);
	const normalizedAssignedUserIdsKey = useMemo(
		() => Array.from(new Set(
			assignedUserIds
				.map((value) => normalizeUserId(value))
				.filter((value): value is number => value !== null)
		)).join(','),
		[assignedUserIds]
	);
	const normalizedAssignedUserIds = useMemo(
		() => normalizedAssignedUserIdsKey
			? normalizedAssignedUserIdsKey
				.split(',')
				.map((value) => Number.parseInt(value, 10))
				.filter((value): value is number => Number.isFinite(value))
			: [],
		[normalizedAssignedUserIdsKey]
	);
	const selectedDateStr = useMemo(
		() => scheduledStart.split('T')[0] || '',
		[scheduledStart]
	);
	const timelineAssignees = useMemo(
		() => normalizedAssignedUserIds.map((userId) => ({
			id: userId,
			name: buildAssigneeDisplayName(availableAssignees.find((assignee) => assignee.id === userId)) || `User #${userId}`,
		})),
		[availableAssignees, normalizedAssignedUserIds]
	);
	const scheduleValidation = useMemo(
		() => getAppointmentScheduleValidation(scheduledStart, scheduledEnd),
		[scheduledEnd, scheduledStart]
	);
	const scheduleError = useMemo(
		() => getAppointmentScheduleErrorMessage(scheduledStart, scheduledEnd),
		[scheduledEnd, scheduledStart]
	);

	useEffect(() => {
		if (!isOpen || normalizedAssignedUserIds.length === 0 || !selectedDateStr) {
			setAvailability((previous) => (Object.keys(previous).length === 0 ? previous : {}));
			setLoadingAvailability((previous) => (previous ? false : previous));
			return;
		}

		let cancelled = false;

		const loadAvailability = async () => {
			setLoadingAvailability(true);
			try {
				const token = authService.getTokens()?.access_token;
				const headers: HeadersInit = { 'Content-Type': 'application/json' };
				if (token) headers.Authorization = `Bearer ${token}`;

				const dayStart = `${selectedDateStr}T00:00:00`;
				const dayEnd = `${selectedDateStr}T23:59:59`;
				const nextAvailability: Record<number, ExistingAppointment[]> = {};

				await Promise.all(normalizedAssignedUserIds.map(async (userId) => {
					try {
						const response = await fetch(
							`/api/v1/work-items?item_type=appointment&assigned_to=${userId}&scheduled_start_from=${dayStart}&scheduled_start_to=${dayEnd}&limit=50`,
							{ headers, credentials: 'include' }
						);

						const data = await response.json();
						nextAvailability[userId] = response.ok && data.success
							? (data.data || [])
								.filter((appointment: ExistingAppointment) => appointment.scheduled_start && appointment.scheduled_end && !isScheduleHiddenAppointmentStatus(appointment.status))
								.map((appointment: ExistingAppointment) => ({
									id: appointment.id,
									title: appointment.title,
									scheduled_start: appointment.scheduled_start,
									scheduled_end: appointment.scheduled_end,
									status: appointment.status,
									item_number: appointment.item_number,
								}))
							: [];
					} catch {
						nextAvailability[userId] = [];
					}
				}));

				if (!cancelled) {
					setAvailability(nextAvailability);
				}
			} catch (loadError) {
				console.error('Error loading assignee day schedules for appointment drop setup:', loadError);
				if (!cancelled) {
					setAvailability((previous) => (Object.keys(previous).length === 0 ? previous : {}));
				}
			} finally {
				if (!cancelled) {
					setLoadingAvailability((previous) => (previous ? false : previous));
				}
			}
		};

		void loadAvailability();

		return () => {
			cancelled = true;
		};
	}, [isOpen, normalizedAssignedUserIds, normalizedAssignedUserIdsKey, selectedDateStr]);

	useEffect(() => {
		if (!isOpen || locationSource !== 'custom') return;

		const styleId = 'pac-z-fix-appointment-drop-setup';
		let styleElement = document.getElementById(styleId) as HTMLStyleElement | null;
		if (!styleElement) {
			styleElement = document.createElement('style');
			styleElement.id = styleId;
			document.head.appendChild(styleElement);
		}

		styleElement.textContent = `
			.pac-container {
				z-index: 10050 !important;
				pointer-events: auto !important;
			}
		`;

		return () => {
			document.getElementById(styleId)?.remove();
		};
	}, [isOpen, locationSource]);

	useEffect(() => {
		if (!isOpen || !resetSnapshot) return;

		setAppointmentType('');
		setAppointmentPriority('');
		setAppointmentTitle(resetSnapshot.appointmentTitle);
		setSelectedLocationId(resetSnapshot.selectedLocationId);
		setSelectedContactPersonId(resetSnapshot.selectedContactPersonId);
		setOverrideContact(null);
		setOverrideLocationId(null);
		setOverrideLocations([]);
		setCustomContactPerson(resetSnapshot.customContactPerson);
		setCustomLocation(resetSnapshot.customLocation);
		setCustomLocationSearch(resetSnapshot.customLocationSearch);
		setPurchaseItems(resetSnapshot.purchaseItems);
		setAdditionalNotes(resetSnapshot.additionalNotes);
		setEquipmentRequired(resetSnapshot.equipmentRequired);
		setSafetyRequirements(resetSnapshot.safetyRequirements);
		setScheduledStart(resetSnapshot.scheduledStart);
		setScheduledEnd(resetSnapshot.scheduledEnd);
		setError(null);
		setSubmitting(false);
		setExpandedRequirements(false);
		setLocationSource(resetSnapshot.locationSource);
	}, [isOpen, resetSnapshot]);

	useEffect(() => {
		if (!isOpen || !isPurchaseAppointmentType || normalizedPurchaseItems.length > 0) {
			return;
		}

		if (inheritedPurchaseItems.length === 0) {
			return;
		}

		setPurchaseItems(inheritedPurchaseItems);
	}, [inheritedPurchaseItems, isOpen, isPurchaseAppointmentType, normalizedPurchaseItems.length]);

	useEffect(() => {
		if (!isOpen) return;
		if (contacts.length > 0) return;

		let cancelled = false;

		const loadContacts = async () => {
			setLoadingContacts(true);
			try {
				const token = authService.getTokens()?.access_token;
				const headers: HeadersInit = { 'Content-Type': 'application/json' };
				if (token) headers.Authorization = `Bearer ${token}`;

				const response = await fetch('/api/v1/contacts?limit=500', {
					headers,
					credentials: 'include',
				});

				if (!response.ok) {
					throw new Error('Failed to load contacts');
				}

				const data = await response.json();
				if (!cancelled) {
					const nextContacts = Array.isArray(data?.data) ? data.data : [];
					setContacts(nextContacts);
				}
			} catch (loadError) {
				console.error('Error loading contacts for appointment drop setup:', loadError);
			} finally {
				if (!cancelled) {
					setLoadingContacts(false);
				}
			}
		};

		loadContacts();

		return () => {
			cancelled = true;
		};
	}, [contacts.length, isOpen]);

	useEffect(() => {
		if (!isOpen || normalizedJobContactId == null) {
			latestInheritedContactDataRequestId.current += 1;
			setContactLocations([]);
			setContactPeople([]);
			return;
		}

		let cancelled = false;
		const requestId = ++latestInheritedContactDataRequestId.current;
		const cachedContactData = inheritedContactDataCacheRef.current.get(normalizedJobContactId);

		if (cachedContactData) {
			setContactLocations(cachedContactData.locations);
			setContactPeople(cachedContactData.people);
			setLoadingContactLocations(false);
			setLoadingContactPeople(false);
			return;
		}

		const loadInheritedData = async () => {
			const token = authService.getTokens()?.access_token;
			const headers: HeadersInit = { 'Content-Type': 'application/json' };
			if (token) headers.Authorization = `Bearer ${token}`;

			setLoadingContactLocations(true);
			setLoadingContactPeople(true);

			try {
				const [locationsResponse, nextPeople] = await Promise.all([
					fetch(`/api/v1/contacts/${normalizedJobContactId}/locations`, {
						headers,
						credentials: 'include',
					}),
					fetchContactPeople<ContactPersonOption>({
						contactId: normalizedJobContactId,
						token,
						activeOnly: true,
						credentials: 'include',
						headers,
					}),
				]);

				const locationsData = await (locationsResponse.ok ? locationsResponse.json() : Promise.resolve({ data: [] }));

				if (!cancelled && requestId === latestInheritedContactDataRequestId.current) {
					const nextLocations = Array.isArray(locationsData?.data)
						? locationsData.data.map((location: ContactLocation) => normalizeContactLocationRecord(location))
						: [];
					inheritedContactDataCacheRef.current.set(normalizedJobContactId, {
						locations: nextLocations,
						people: nextPeople,
					});
					setContactLocations(nextLocations);
					setContactPeople(nextPeople);
				}
			} catch (loadError) {
				console.error('Error loading inherited contact data for appointment drop setup:', loadError);
				if (!cancelled && requestId === latestInheritedContactDataRequestId.current) {
					setContactLocations([]);
					setContactPeople([]);
				}
			} finally {
				if (!cancelled && requestId === latestInheritedContactDataRequestId.current) {
					setLoadingContactLocations(false);
					setLoadingContactPeople(false);
				}
			}
		};

		loadInheritedData();

		return () => {
			cancelled = true;
		};
	}, [isOpen, normalizedJobContactId]);

	useEffect(() => {
		if (loadingContactPeople) {
			return;
		}

		const currentSelectedPersonId = normalizeLocationId(selectedContactPersonId);
		if (currentSelectedPersonId != null && !contactPeople.some((person) => normalizeLocationId(person.id) === currentSelectedPersonId)) {
			setSelectedContactPersonId(contactPeople[0]?.id ?? null);
		}
	}, [contactPeople, loadingContactPeople, selectedContactPersonId]);

	useEffect(() => {
		if (loadingOverrideLocations) {
			return;
		}

		const currentOverrideLocationId = normalizeLocationId(overrideLocationId);
		if (currentOverrideLocationId != null && !overrideLocations.some((location) => normalizeLocationId(location.id) === currentOverrideLocationId)) {
			setOverrideLocationId(null);
		}
	}, [loadingOverrideLocations, overrideLocationId, overrideLocations]);

	useEffect(() => {
		if (loadingOverrideContactPeople) {
			return;
		}

		const currentOverridePersonId = normalizeLocationId(selectedContactPersonId);
		if (currentOverridePersonId != null && !overrideContactPeople.some((person) => normalizeLocationId(person.id) === currentOverridePersonId)) {
			setSelectedContactPersonId(null);
		}
	}, [loadingOverrideContactPeople, overrideContactPeople, selectedContactPersonId]);

	useEffect(() => {
		if (!isOpen || locationSource !== 'contact') {
			return;
		}

		const preferredLocation = findPreferredContactLocation(contactLocations, selectedLocationId, normalizedJobLocationId);
		const preferredLocationId = normalizeLocationId(preferredLocation?.id);
		const currentSelectedLocationId = normalizeLocationId(selectedLocationId);

		if (preferredLocationId !== currentSelectedLocationId) {
			setSelectedLocationId(preferredLocationId);
		}
	}, [contactLocations, isOpen, locationSource, normalizedJobLocationId, selectedLocationId]);

	useEffect(() => {
		if (!error) {
			return;
		}

		if (locationSource === 'contact' && inheritedLocation && getContactLocationCoordinates(inheritedLocation)) {
			setError(null);
			return;
		}

		if (locationSource === 'different_contact' && resolvedDifferentLocation && getContactLocationCoordinates(resolvedDifferentLocation)) {
			setError(null);
		}
	}, [error, inheritedLocation, locationSource, resolvedDifferentLocation]);

	const handleLocationSourceChange = (nextSource: LocationSource) => {
		setLocationSource(nextSource);
		setError(null);

		if (nextSource === 'contact') {
			setSelectedLocationId(normalizedJobLocationId);
			setSelectedContactPersonId(job?.contact_person_id ?? null);
			setOverrideContact(null);
			setOverrideLocationId(null);
			setOverrideLocations([]);
			setOverrideContactPeople([]);
			setCustomLocation(null);
			setCustomLocationSearch('');
			setCustomContactPerson(job?.contact_person || '');
			return;
		}

		if (nextSource === 'different_contact') {
			setSelectedLocationId(null);
			setSelectedContactPersonId(null);
			setOverrideContact(null);
			setOverrideLocationId(null);
			setOverrideLocations([]);
			setOverrideContactPeople([]);
			setCustomLocation(null);
			setCustomLocationSearch('');
			setCustomContactPerson('');
			return;
		}

		setSelectedLocationId(null);
		setSelectedContactPersonId(null);
		setOverrideContact(null);
		setOverrideLocationId(null);
		setOverrideLocations([]);
		setOverrideContactPeople([]);
		setCustomContactPerson(job?.contact_person || '');
	};

	const loadOverrideContactData = async (contactId: number) => {
		activeOverrideContactIdRef.current = contactId;
		const locationsRequestId = ++latestOverrideLocationsLoadRequestId.current;
		const peopleRequestId = ++latestOverridePeopleLoadRequestId.current;
		const cachedContactData = overrideContactDataCacheRef.current.get(contactId);

		if (cachedContactData) {
			setOverrideLocations(cachedContactData.locations);
			setOverrideContactPeople(cachedContactData.people);
			setLoadingOverrideLocations(false);
			setLoadingOverrideContactPeople(false);
			return;
		}

		const token = authService.getTokens()?.access_token;
		const headers: HeadersInit = { 'Content-Type': 'application/json' };
		if (token) headers.Authorization = `Bearer ${token}`;

		setLoadingOverrideLocations(true);
		setLoadingOverrideContactPeople(true);

		try {
			const [locationsResponse, nextPeople] = await Promise.all([
				fetch(`/api/v1/contacts/${contactId}/locations`, { headers, credentials: 'include' }),
				fetchContactPeople<ContactPersonOption>({
					contactId,
					token,
					activeOnly: true,
					credentials: 'include',
					headers,
				}),
			]);

			const locationsData = await (locationsResponse.ok ? locationsResponse.json() : Promise.resolve({ data: [] }));

			const nextLocations = Array.isArray(locationsData?.data)
				? locationsData.data.map((location: ContactLocation) => normalizeContactLocationRecord(location))
				: [];
			overrideContactDataCacheRef.current.set(contactId, {
				locations: nextLocations,
				people: nextPeople,
			});

			if (locationsRequestId === latestOverrideLocationsLoadRequestId.current && activeOverrideContactIdRef.current === contactId) {
				setOverrideLocations(nextLocations);
			}

			if (peopleRequestId === latestOverridePeopleLoadRequestId.current && activeOverrideContactIdRef.current === contactId) {
				setOverrideContactPeople(nextPeople);
			}
		} catch (loadError) {
			console.error('Error loading override contact data for appointment drop setup:', loadError);
			if (locationsRequestId === latestOverrideLocationsLoadRequestId.current && activeOverrideContactIdRef.current === contactId) {
				setOverrideLocations([]);
			}
			if (peopleRequestId === latestOverridePeopleLoadRequestId.current && activeOverrideContactIdRef.current === contactId) {
				setOverrideContactPeople([]);
			}
		} finally {
			if (locationsRequestId === latestOverrideLocationsLoadRequestId.current && activeOverrideContactIdRef.current === contactId) {
				setLoadingOverrideLocations(false);
			}
			if (peopleRequestId === latestOverridePeopleLoadRequestId.current && activeOverrideContactIdRef.current === contactId) {
				setLoadingOverrideContactPeople(false);
			}
		}
	};

	const handleSharedOverrideContactChange = (contactId: number | null, contactName?: string) => {
		setError(null);
		setOverrideLocationId(null);
		setSelectedContactPersonId(null);

		if (!contactId) {
			latestOverrideLocationsLoadRequestId.current += 1;
			latestOverridePeopleLoadRequestId.current += 1;
			activeOverrideContactIdRef.current = null;
			setOverrideContact(null);
			setOverrideLocations([]);
			setOverrideContactPeople([]);
			setLoadingOverrideLocations(false);
			setLoadingOverrideContactPeople(false);
			return;
		}

		const nextContact = contacts.find((contact) => contact.id === contactId) || {
			id: contactId,
			company_name: contactName || null,
			contact_person: contactName || null,
			email: null,
			phone: null,
		};

		setContacts((previous) => previous.some((contact) => contact.id === contactId) ? previous : [nextContact, ...previous]);
		setOverrideContact(nextContact);
		void loadOverrideContactData(contactId);
	};

	const handleSharedOverrideLocationChange = (locationId: number | null) => {
		setOverrideLocationId(locationId);
		setError(null);
	};

	const handleSharedOverrideContactPersonChange = (personId: number | null) => {
		setSelectedContactPersonId(personId);
		setError(null);
	};

	const handleSharedOverrideUnifiedSelect = (result: UnifiedLookupSearchResult) => {
		handleSharedOverrideContactChange(result.contact_id, result.contact_company_name || result.contact_name);
		setOverrideLocationId(result.location_id);
		setSelectedContactPersonId(result.person_id);
		setError(null);
	};

	const handleSharedOverrideLocationCreated = (newLocation: UnifiedLookupLocation) => {
		setOverrideLocations((previous) => {
			const filtered = previous.filter((location) => location.id !== newLocation.id);
			return [
				{
					id: newLocation.id,
					location_name: newLocation.location_name,
					address_line1: newLocation.address_line1,
					address_line2: newLocation.address_line2,
					city: newLocation.city,
					province: newLocation.state,
					postal_code: newLocation.postal_code,
					country: newLocation.country,
					latitude: null,
					longitude: null,
					coordinates: null,
				},
				...filtered,
			];
		});
		setOverrideLocationId(newLocation.id);
		setError(null);
	};

	const handleSharedOverridePersonCreated = (newPerson: UnifiedLookupContactPerson) => {
		setOverrideContactPeople((previous) => {
			const filtered = previous.filter((person) => person.id !== newPerson.id);
			return [
				{
					id: newPerson.id,
					first_name: newPerson.first_name,
					last_name: newPerson.last_name,
					email: newPerson.email,
					phone: newPerson.phone,
					position: newPerson.job_title,
				},
				...filtered,
			];
		});
		setSelectedContactPersonId(newPerson.id);
		setError(null);
	};

	const validateSelection = (finalizedPurchaseItems: PurchaseListItem[]): AppointmentDropSetupSelection | null => {
		if (!job) {
			setError('No job is selected for appointment creation.');
			return null;
		}

		if (!appointmentType) {
			setError('Please choose an appointment type.');
			return null;
		}

		if (!appointmentPriority) {
			setError('Please choose an appointment priority.');
			return null;
		}

		if (!appointmentTitle.trim()) {
			setError('Please enter an appointment title.');
			return null;
		}

		if (!scheduleValidation.isValid) {
			setError(scheduleError || 'Please correct the appointment schedule before continuing.');
			return null;
		}

		const normalizedScheduledStartISO = formatLocalISOSAST(new Date(scheduledStart));
		const normalizedScheduledEndISO = formatLocalISOSAST(new Date(scheduledEnd));

		const selectionPurchaseItems = isPurchaseAppointmentType
			? normalizePurchaseListItems(finalizedPurchaseItems)
			: [];

		if (locationSource === 'contact') {
			if (loadingContactLocations && contactLocations.length === 0 && normalizedJobLocationId != null) {
				setError('Saved contact locations are still loading. Please wait a moment and try again.');
				return null;
			}

			const resolvedInheritedLocation = inheritedLocation || findPreferredContactLocation(contactLocations, selectedLocationId, normalizedJobLocationId);

			if (resolvedInheritedLocation) {
				const inheritedCoordinates = getContactLocationCoordinates(resolvedInheritedLocation);
				if (!inheritedCoordinates) {
					setError(buildMissingCoordinatesMessage(resolvedInheritedLocation.location_name || formatLocationAddress(resolvedInheritedLocation) || 'selected job location'));
					return null;
				}

				return {
					appointmentTitle: appointmentTitle.trim(),
					appointmentType,
					appointmentPriority,
					scheduledStartISO: normalizedScheduledStartISO,
					scheduledEndISO: normalizedScheduledEndISO,
					purchaseItems: selectionPurchaseItems,
					additionalNotes,
					equipmentRequired,
					safetyRequirements,
					resolvedContactId: job.contact_id ?? null,
					resolvedContactName: job.contact_name || null,
					resolvedContactEmail: (job as any).contact_email || null,
					resolvedContactPhone: (job as any).contact_phone || null,
					resolvedLocationId: resolvedInheritedLocation.id,
					resolvedLocationName: resolvedInheritedLocation.location_name || null,
					resolvedLocationAddress: formatLocationAddress(resolvedInheritedLocation) || null,
					resolvedCoordinates: inheritedCoordinates,
					resolvedContactPersonId: inheritedContactPerson?.id ?? job.contact_person_id ?? null,
					resolvedContactPerson:
						buildName(inheritedContactPerson?.name, inheritedContactPerson?.first_name, inheritedContactPerson?.last_name) ||
						job.contact_person ||
						null,
					resolvedContactPersonEmail: inheritedContactPerson?.email || null,
					resolvedContactPersonPhone: inheritedContactPerson?.phone || null,
				};
			}

			if (contactLocations.length === 0 && !job.location_id && (job.location_name || job.location_address)) {
				const inheritedCoordinates = extractCoordinates(job.location_coordinates);
				if (!inheritedCoordinates) {
					setError(buildMissingCoordinatesMessage(job.location_name || job.location_address || 'job location'));
					return null;
				}

				return {
					appointmentTitle: appointmentTitle.trim(),
					appointmentType,
					appointmentPriority,
					scheduledStartISO: normalizedScheduledStartISO,
					scheduledEndISO: normalizedScheduledEndISO,
					purchaseItems: selectionPurchaseItems,
					additionalNotes,
					equipmentRequired,
					safetyRequirements,
					resolvedContactId: job.contact_id ?? null,
					resolvedContactName: job.contact_name || null,
					resolvedContactEmail: (job as any).contact_email || null,
					resolvedContactPhone: (job as any).contact_phone || null,
					resolvedLocationId: job.location_id ?? null,
					resolvedLocationName: job.location_name || null,
					resolvedLocationAddress: job.location_address || null,
					resolvedCoordinates: inheritedCoordinates,
					resolvedContactPersonId: inheritedContactPerson?.id ?? job.contact_person_id ?? null,
					resolvedContactPerson:
						buildName(inheritedContactPerson?.name, inheritedContactPerson?.first_name, inheritedContactPerson?.last_name) ||
						job.contact_person ||
						null,
					resolvedContactPersonEmail: inheritedContactPerson?.email || null,
					resolvedContactPersonPhone: inheritedContactPerson?.phone || null,
				};
			}

			if (normalizedJobLocationId != null) {
				setError('We could not resolve the saved job location yet. Please re-select the location once it finishes loading.');
				return null;
			}

			setError('Please choose one of the job contact locations, or switch to a custom location.');
			return null;
		}

		if (locationSource === 'different_contact') {
			if (!overrideContact) {
				setError('Please choose the contact to use for this appointment.');
				return null;
			}

			if (!selectedLocation) {
				setError('Please choose a saved location for the selected contact.');
				return null;
			}

			const differentContactCoordinates = getContactLocationCoordinates(selectedLocation);
			if (!differentContactCoordinates) {
				setError(buildMissingCoordinatesMessage(selectedLocation.location_name || formatLocationAddress(selectedLocation) || 'selected contact location'));
				return null;
			}

			return {
				appointmentTitle: appointmentTitle.trim(),
				appointmentType,
				appointmentPriority,
				scheduledStartISO: normalizedScheduledStartISO,
				scheduledEndISO: normalizedScheduledEndISO,
				purchaseItems: selectionPurchaseItems,
				additionalNotes,
				equipmentRequired,
				safetyRequirements,
				resolvedContactId: overrideContact.id,
				resolvedContactName: buildContactDisplayName(overrideContact) || null,
				resolvedContactEmail: overrideContact.email || null,
				resolvedContactPhone: overrideContact.phone || null,
				resolvedLocationId: selectedLocation.id,
				resolvedLocationName: selectedLocation.location_name || null,
				resolvedLocationAddress: formatLocationAddress(selectedLocation) || null,
				resolvedCoordinates: differentContactCoordinates,
				resolvedContactPersonId: resolvedDifferentContactPerson?.id ?? null,
				resolvedContactPerson: resolvedDifferentContactPerson
					? buildName(resolvedDifferentContactPerson.name, resolvedDifferentContactPerson.first_name, resolvedDifferentContactPerson.last_name) || null
					: null,
				resolvedContactPersonEmail: resolvedDifferentContactPerson?.email || null,
				resolvedContactPersonPhone: resolvedDifferentContactPerson?.phone || null,
			};
		}

		if (!customLocation?.name.trim()) {
			setError('Please search and select a custom location from the map so we can capture coordinates.');
			return null;
		}

		const selectedCustomLocation = customLocation;
		const customCoordinates = extractCoordinates({ lat: selectedCustomLocation.lat, lng: selectedCustomLocation.lng });

		if (!customCoordinates) {
			setError('The selected custom location is missing coordinates. Please choose a map result again.');
			return null;
		}

		return {
			appointmentTitle: appointmentTitle.trim(),
			appointmentType,
			appointmentPriority,
			scheduledStartISO: normalizedScheduledStartISO,
			scheduledEndISO: normalizedScheduledEndISO,
			purchaseItems: selectionPurchaseItems,
			additionalNotes,
			equipmentRequired,
			safetyRequirements,
			resolvedContactId: job.contact_id ?? null,
			resolvedContactName: job.contact_name || null,
			resolvedContactEmail: (job as any).contact_email || null,
			resolvedContactPhone: (job as any).contact_phone || null,
			resolvedLocationId: null,
			resolvedLocationName: selectedCustomLocation.name.trim(),
			resolvedLocationAddress: selectedCustomLocation.address.trim() || null,
			resolvedCoordinates: customCoordinates,
			resolvedContactPersonId: null,
			resolvedContactPerson: customContactPerson.trim() || job.contact_person || null,
		};
	};

	const handleConfirm = async () => {
		if (!canCreate) {
			setError('You do not have permission to create appointments.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const finalizedPurchaseItems = isPurchaseAppointmentType
				? purchaseItemListRef.current?.commitPendingCapture() ?? normalizedPurchaseItems
				: [];
			const selection = validateSelection(finalizedPurchaseItems);
			if (!selection) {
				return;
			}

			// Account standing warning (does not gate)
			const warnContactId = selection.resolvedContactId ?? job?.contact_id ?? null;
			if (warnContactId) {
				const { warnIfContactAccountIssue } = await import('@/lib/contacts/accountWarning');
				const proceed = await warnIfContactAccountIssue({
					contactId: warnContactId,
					action: 'schedule this appointment',
				});
				if (!proceed) {
					return;
				}
			}

			await onConfirm(selection);
		} catch (confirmError) {
			const message = confirmError instanceof Error ? confirmError.message : 'Failed to create the appointment.';
			setError(message);
		} finally {
			setSubmitting(false);
		}
	};

	if (!isOpen || !job) return null;

	return (
		<>
		{submitting && typeof document !== 'undefined'
			? createPortal(
				<div
					className="fixed inset-0 flex items-center justify-center bg-slate-900/55 backdrop-blur-sm dark:bg-slate-950/60"
					style={{ zIndex: APPOINTMENT_CREATE_LOADING_OVERLAY_Z }}
					role="status"
					aria-live="polite"
					aria-busy="true"
				>
					<BrandedLoadingSpinner
						size="lg"
						message="Creating appointment…"
						subMessage="Please keep this window open."
						primaryColor="#059669"
						showSurface
						showProgressBar
					/>
				</div>,
				document.body,
			)
			: null}
		<Dialog open={isOpen} onOpenChange={(open) => { if (!open && !submitting) onClose(); }}>
			<DialogContent
				className="flex w-[min(calc(100vw-1rem),64rem)] max-w-4xl flex-col gap-0 overflow-hidden border border-gray-200 border-t-4 border-t-green-500 bg-white p-0 text-gray-900 shadow-2xl [&>button]:hidden dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
				style={{
					maxHeight: `calc(var(--100dvh, 100vh) - ${MOBILE_SAFE_AREA_TOP} - ${MOBILE_SAFE_AREA_BOTTOM} - 1rem)`,
				}}
				onInteractOutside={(event) => {
					if (submitting) {
						event.preventDefault();
						return;
					}
					const target = event.target as HTMLElement;
					if (target.closest('.pac-container')) {
						event.preventDefault();
					}
				}}
				onEscapeKeyDown={(event) => {
					if (submitting) {
						event.preventDefault();
					}
				}}
			>
				<DialogHeader className="sr-only">
					<DialogTitle>Create appointment from job</DialogTitle>
					<DialogDescription>
						Choose the appointment type and confirm the location details before the drop creates the child appointment.
					</DialogDescription>
				</DialogHeader>
				<div className="px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-500 text-white">
					<div className="flex items-center justify-between gap-4">
						<div className="flex items-center gap-3">
							<div className="p-2.5 bg-white/20 backdrop-blur rounded-xl shadow-lg">
								<CalendarClock className="w-6 h-6" />
							</div>
							<div>
								<div className="flex items-center gap-2">
									<h2 className="text-xl font-bold">Create Appointment</h2>
									<span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-white/20 rounded-full">Job Drop</span>
								</div>
								<p className="text-sm text-white/80">Capture appointment type and location details before scheduling the child appointment.</p>
							</div>
						</div>
						<button
							type="button"
							onClick={onClose}
							disabled={submitting}
							className={`p-2 rounded-lg transition-colors ${submitting ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10'}`}
							aria-label="Close appointment setup modal"
						>
							<X className="w-5 h-5" />
						</button>
					</div>
				</div>

				<div
					className="flex-1 min-h-0 space-y-6 overflow-y-auto p-4 sm:p-6"
					style={{
						paddingBottom: `calc(${MOBILE_SAFE_AREA_BOTTOM} + 1rem)`,
					}}
				>
				<div className={sectionCardClassName}>
					<div className="flex items-start justify-between gap-4">
						<div>
							<p className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{job.subject || job.title || 'Untitled job'}</p>
							<p className={`mt-1 ${subtleTextClassName}`}>Choose the appointment type, adjust the schedule if needed, and confirm the location details before the drop creates the child appointment.</p>
						</div>
						<div className={`rounded-full px-3 py-1 text-xs font-medium shadow-sm ${isDark ? 'bg-gray-800 text-gray-200 border border-gray-700' : 'bg-white text-gray-700'}`}>
							Job #{job.item_number || job.id}
						</div>
					</div>

					<div className={`mt-4 flex items-start gap-2 ${bodyTextClassName}`}>
						<CalendarClock className="mt-0.5 h-4 w-4 text-green-500" />
						<div>
							<div className="font-medium">Suggested slot</div>
							<div>
								{scheduledStartISO ? formatDateTimeSAST(scheduledStartISO, {
									year: 'numeric',
									month: 'short',
									day: 'numeric',
									hour: '2-digit',
									minute: '2-digit',
									hour12: false,
								}) : 'No suggested slot'}
								{scheduledEndISO
									? ` → ${formatDateTimeSAST(scheduledEndISO, {
											hour: '2-digit',
											minute: '2-digit',
											hour12: false,
										})}`
									: ''}
							</div>
							<div className={`mt-1 ${subtleTextClassName}`}>You can change the date and time below before creating the appointment.</div>
						</div>
					</div>
				</div>

				<div className="space-y-2">
					<div>
						<label className={labelClassName}>Appointment title <span className="text-red-500">*</span></label>
						<input
							type="text"
							value={appointmentTitle}
							onChange={(event) => {
								setAppointmentTitle(event.target.value);
								setError(null);
							}}
							className={fieldClassName}
							placeholder="Enter appointment title"
						/>
						<p className={`mt-1 ${subtleTextClassName}`}>
							Inherited from the job by default, but fully editable for this appointment.
						</p>
					</div>

					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						<div>
							<label className={labelClassName}>Appointment type <span className="text-red-500">*</span></label>
							<select
								value={appointmentType}
								onChange={(event) => {
									setAppointmentType(event.target.value);
									setError(null);
								}}
								className={fieldClassName}
							>
								<option value="">Select type...</option>
								{appointmentTypeOptions.map((option) => (
									<option key={option.key} value={option.key}>
										{option.label} {option.visible_to_customer !== false ? '🟢 Visible to customer' : '🔴 Not visible to customer'}
									</option>
								))}
							</select>
						</div>

						<div>
							<label className={labelClassName}>Priority <span className="text-red-500">*</span></label>
							<select
								value={appointmentPriority}
								onChange={(event) => {
									setAppointmentPriority(event.target.value);
									setError(null);
								}}
								className={fieldClassName}
							>
								<option value="">Select priority...</option>
								{APPOINTMENT_PRIORITIES.map((priority) => (
									<option key={priority.value} value={priority.value}>
										{priority.icon} {priority.label}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>

				<div className={sectionCardClassName}>
					<label className={labelClassName}>
						<div className="flex items-center gap-2">
							<CalendarClock className="w-4 h-4" />
							<span>Schedule <span className="text-red-500">*</span></span>
						</div>
					</label>
					<p className={`mt-1 mb-3 ${subtleTextClassName}`}>This mirrors the main appointment editor, so you can adjust both start and finish before the appointment is created.</p>
					<AppointmentScheduler
						scheduledStart={scheduledStart}
						scheduledEnd={scheduledEnd}
						onChange={(next) => {
							setScheduledStart(next.scheduled_start);
							setScheduledEnd(next.scheduled_end);
							setError(null);
						}}
						isDark={isDark}
					/>
					<div className={`mt-2 text-xs ${scheduleError ? (isDark ? 'text-red-300' : 'text-red-600') : (isDark ? 'text-gray-500' : 'text-gray-500')}`}>
						{scheduleError || 'Appointments default to 1 hour and cannot exceed 24 hours.'}
					</div>
				</div>

					{timelineAssignees.length > 0 && selectedDateStr ? (
						<div className={sectionCardClassName}>
							<div className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
								<CalendarClock className="w-4 h-4" />
								<span>
									Day Schedule — {new Date(`${selectedDateStr}T00:00:00`).toLocaleDateString('en-ZA', {
										weekday: 'long',
										year: 'numeric',
										month: 'long',
										day: 'numeric',
										timeZone: SAST_TIMEZONE,
									})}
								</span>
							</div>
							<p className={`mb-3 ${subtleTextClassName}`}>
								A quick clash preview for the selected resource{timelineAssignees.length === 1 ? '' : 's'} before the appointment gets created.
							</p>
							{loadingAvailability ? (
								<div className={`flex items-center gap-2 text-sm py-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
									<Loader2 className="w-4 h-4 animate-spin" /> Loading schedules...
								</div>
							) : (
								<div>
									{timelineAssignees.map((assignee) => (
										<DayScheduleTimeline
											key={assignee.id}
											memberName={assignee.name}
											appointments={availability[assignee.id] || []}
											proposedStart={scheduledStart}
											proposedEnd={scheduledEnd}
											isDark={isDark}
										/>
									))}
									<div className={`flex items-center gap-4 mt-3 pt-2 border-t text-[11px] ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
										<div className="flex items-center gap-1.5">
											<div className={`w-3 h-2.5 rounded-sm ${isDark ? 'bg-amber-700/70 border border-amber-600' : 'bg-amber-200 border border-amber-300'}`} />
											<span>Existing</span>
										</div>
										<div className="flex items-center gap-1.5">
											<div className={`w-3 h-2.5 rounded-sm border-2 border-dashed ${isDark ? 'bg-green-500/25 border-green-400' : 'bg-green-500/20 border-green-500'}`} />
											<span>Proposed</span>
										</div>
										<div className="flex items-center gap-1.5">
											<div className="w-3 h-2.5 rounded-sm bg-red-500/20 border-2 border-dashed border-red-500" />
											<span>Conflict</span>
										</div>
									</div>
								</div>
							)}
						</div>
					) : null}

				{isPurchaseAppointmentType ? (
					<PurchaseItemList
						ref={purchaseItemListRef}
						items={normalizedPurchaseItems}
						onChange={setPurchaseItems}
						isDark={isDark}
						mode="edit"
						title="Itemized Purchase List"
						helperText="Capture the expected shopping list for this purchase appointment before it is created from the job drop."
					/>
				) : null}

					<OptionalAccordionSection
						title="Requirements"
						description="Equipment, safety requirements, and extra appointment notes inherited from the job when available."
						icon={<Info className="h-4 w-4" />}
						isOpen={expandedRequirements}
						onToggle={() => setExpandedRequirements((previous) => !previous)}
						isDark={isDark}
					>
						<div className="space-y-4">
							{!isPurchaseAppointmentType ? (
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<div>
										<label className={labelClassName}>Equipment required</label>
										<textarea
											value={equipmentRequired}
											onChange={(event) => {
												setEquipmentRequired(event.target.value);
												setError(null);
											}}
											rows={3}
											className={fieldClassName}
											placeholder="e.g. Ladder, drill, multimeter..."
										/>
									</div>
									<div>
										<label className={labelClassName}>Safety requirements</label>
										<textarea
											value={safetyRequirements}
											onChange={(event) => {
												setSafetyRequirements(event.target.value);
												setError(null);
											}}
											rows={3}
											className={fieldClassName}
											placeholder="e.g. Hard hat, safety glasses, gloves..."
										/>
									</div>
								</div>
							) : null}

							<div>
								<label className={labelClassName}>Additional notes</label>
								<textarea
									value={additionalNotes}
									onChange={(event) => {
										setAdditionalNotes(event.target.value);
										setError(null);
									}}
									rows={3}
									className={fieldClassName}
									placeholder="Inherited additional notes from the job..."
								/>
							</div>
						</div>
					</OptionalAccordionSection>

				<div className={sectionCardClassName}>
					<h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
						<User className="w-5 h-5 text-blue-600" />
						Customer &amp; Location
					</h3>
					<p className={subtleTextClassName}>Match the appointment details to the job, switch to another contact, or capture a custom site now so nobody has to play detective later.</p>

					<div className="grid grid-cols-1 gap-3 mt-4 md:grid-cols-3">
						<div className={`p-3 rounded-lg border ${
							activeSelectedContact
								? (isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200')
								: (isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed')
						}`}>
							<div className="flex items-center gap-2">
								<Building2 className={`w-4 h-4 ${activeSelectedContact ? 'text-blue-500' : 'text-gray-400'}`} />
								<div className="flex-1 min-w-0">
									<div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contact *</div>
									<div className={`text-sm font-medium truncate ${activeSelectedContact ? (isDark ? 'text-gray-200' : 'text-gray-800') : 'text-gray-400'}`}>
										{activeSelectedContact ? (activeSelectedContact.company_name || activeSelectedContact.contact_person || 'Not selected') : 'Not selected'}
									</div>
									<div className={`text-[10px] mt-0.5 ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
										{locationSource === 'different_contact' ? 'Selected for this appointment' : 'Inherited from job'}
									</div>
								</div>
							</div>
						</div>

						<div className={`p-3 rounded-lg border ${
							hasValidLocation
								? (isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200')
								: (isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed')
						}`}>
							<div className="flex items-center gap-2">
								<MapPin className={`w-4 h-4 ${hasValidLocation ? 'text-green-500' : 'text-gray-400'}`} />
								<div className="flex-1 min-w-0">
									<div className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
										Location *
										{hasCustomLoc ? (
											<span className="px-1 text-[10px] bg-orange-500/20 text-orange-500 rounded font-medium">custom</span>
										) : null}
									</div>
									{hasCustomLoc ? (
										<div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{customLocation!.name}</div>
									) : hasContactLoc ? (
										<div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLocation!.location_name}</div>
									) : activeSelectedContact ? (
										locationSource === 'contact' && contactLocations.length > 0 ? (
											<div className="flex items-center gap-1">
												<select
													value={selectedLocationId ?? ''}
													onChange={(event) => {
														setSelectedLocationId(event.target.value ? Number(event.target.value) : null);
														setError(null);
													}}
													className={`flex-1 text-xs py-1 px-1 rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
												>
													<option value="">Select...</option>
													{contactLocations.map((location) => (
														<option key={location.id} value={location.id}>{location.location_name}</option>
													))}
												</select>
											</div>
										) : (
											<div className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Set below ↓</div>
										)
									) : (
										<div className="text-sm text-gray-400">Select contact first</div>
									)}
								</div>
								{hasCustomLoc ? (
									<button
										type="button"
										onClick={() => {
											setCustomLocation(null);
											setCustomLocationSearch('');
											setError(null);
										}}
										className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
									>
										<X className="w-3 h-3 text-gray-400" />
									</button>
								) : hasContactLoc ? (
									<button
										type="button"
										onClick={() => {
											if (locationSource === 'different_contact') {
												setOverrideLocationId(null);
											} else {
												setSelectedLocationId(null);
											}
											setError(null);
										}}
										className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
									>
										<X className="w-3 h-3 text-gray-400" />
									</button>
								) : null}
							</div>
						</div>

						<div className={`p-3 rounded-lg border ${
							selectedPerson
								? (isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200')
								: (isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed')
						}`}>
							<div className="flex items-center gap-2">
								<User className={`w-4 h-4 ${selectedPerson ? 'text-purple-500' : 'text-gray-400'}`} />
								<div className="flex-1 min-w-0">
									<div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contact Person</div>
									{selectedPerson ? (
										<div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
											{buildName(selectedPerson.name, selectedPerson.first_name, selectedPerson.last_name)}
										</div>
									) : activeSelectedContact ? (
										locationSource === 'different_contact' ? (
											loadingOverrideContactPeople ? (
												<div className="flex items-center gap-1 text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin" />Loading...</div>
											) : (
												<div className="text-sm text-gray-400">Optional</div>
											)
										) : loadingContactPeople ? (
											<div className="flex items-center gap-1 text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin" />Loading...</div>
										) : (
											<select
												value=""
												onChange={(event) => {
													setSelectedContactPersonId(event.target.value ? Number(event.target.value) : null);
													setError(null);
												}}
												className={`w-full text-xs py-1 px-1 rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
											>
												<option value="">Optional...</option>
												{contactPeople.map((person) => (
													<option key={person.id} value={person.id}>
														{buildName(person.name, person.first_name, person.last_name) || `Person #${person.id}`}
													</option>
												))}
											</select>
										)
									) : (
										<div className="text-sm text-gray-400">Optional</div>
									)}
								</div>
								{selectedPerson ? (
									<button type="button" onClick={() => { setSelectedContactPersonId(null); setError(null); }} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
										<X className="w-3 h-3 text-gray-400" />
									</button>
								) : null}
							</div>
						</div>
					</div>
				</div>

				<div className={`${detailPanelClassName} space-y-4`}>
					<label className={labelClassName}>
						<div className="flex items-center gap-2">
							<MapPin className="w-4 h-4" />
							<span>Appointment Location <span className="text-red-500">*</span></span>
						</div>
					</label>

					<div className={`flex p-1 rounded-lg gap-1 ${isDark ? 'bg-gray-700/60' : 'bg-gray-100'}`}>
						<button
							type="button"
							onClick={() => handleLocationSourceChange('contact')}
							className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all ${
								locationSource === 'contact'
									? (isDark ? 'bg-gray-600 text-white shadow-sm' : 'bg-white text-gray-800 shadow-sm')
									: (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
							}`}
						>
							<Building2 className="w-3 h-3" />
							Use job details
						</button>
						<button
							type="button"
							onClick={() => handleLocationSourceChange('different_contact')}
							className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all ${
								locationSource === 'different_contact'
									? (isDark ? 'bg-blue-600 text-white shadow-sm' : 'bg-blue-500 text-white shadow-sm')
									: (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
							}`}
						>
							<Building2 className="w-3 h-3" />
							Different contact
						</button>
						<button
							type="button"
							onClick={() => handleLocationSourceChange('custom')}
							className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all ${
								locationSource === 'custom'
									? (isDark ? 'bg-orange-600 text-white shadow-sm' : 'bg-orange-500 text-white shadow-sm')
									: (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
							}`}
						>
							<MapPin className="w-3 h-3" />
							Custom location
						</button>
					</div>

					{locationSource === 'contact' ? (
						<div className="space-y-4">
							<div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
								isDark ? 'bg-emerald-900/20 border border-emerald-700/50 text-emerald-300' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
							}`}>
								<Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
								<span>These contact details are inherited from the selected job. You can still choose a different saved job location or contact person below.</span>
							</div>

							<div>
								<label className={labelClassName}>Saved location</label>
								{loadingContactLocations ? (
									<div className={`mt-2 flex items-center gap-2 ${bodyTextClassName}`}>
										<Loader2 className="h-4 w-4 animate-spin" /> Loading saved locations…
									</div>
								) : contactLocations.length > 0 ? (
									<select
										value={selectedLocationId ?? ''}
										onChange={(event) => {
											setSelectedLocationId(event.target.value ? Number(event.target.value) : null);
											setError(null);
										}}
										className={fieldClassName}
									>
										<option value="">Select location...</option>
										{contactLocations.map((location) => (
											<option key={location.id} value={location.id}>
												{location.location_name || `Location #${location.id}`}
												{formatLocationAddress(location) ? ` — ${formatLocationAddress(location)}` : ''}
											</option>
										))}
									</select>
								) : job.location_id || job.location_name || job.location_address ? (
									<div className={`mt-2 rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/60 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
										<div className="font-medium">{job.location_name || 'Saved contact location'}</div>
										{job.location_address ? <div className={`mt-1 ${subtleTextClassName}`}>{job.location_address}</div> : null}
									</div>
								) : (
									<div className={`mt-2 rounded-lg border p-3 text-sm ${isDark ? 'border-amber-700/40 bg-amber-900/20 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
										This job does not have a saved contact location. Switch to <span className="font-medium">Custom location</span> if you need to capture a site now.
									</div>
								)}
							</div>

							<div>
								<label className={labelClassName}>Contact person</label>
								{loadingContactPeople ? (
									<div className={`mt-2 flex items-center gap-2 ${bodyTextClassName}`}>
										<Loader2 className="h-4 w-4 animate-spin" /> Loading contact people…
									</div>
								) : contactPeople.length > 0 ? (
									<select
										value={selectedContactPersonId ?? ''}
										onChange={(event) => {
											setSelectedContactPersonId(event.target.value ? Number(event.target.value) : null);
											setError(null);
										}}
										className={fieldClassName}
									>
										<option value="">No specific person</option>
										{contactPeople.map((person) => {
											const name = buildName(person.name, person.first_name, person.last_name) || `Person #${person.id}`;
											return (
												<option key={person.id} value={person.id}>
													{name}
												</option>
											);
										})}
									</select>
								) : (
									<p className={`mt-2 ${bodyTextClassName}`}>{inheritedContactPersonName || 'No contact person linked to this job'}</p>
								)}
							</div>
						</div>
					) : null}

					{locationSource === 'different_contact' ? (
						<div className="space-y-3">
							<div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
								isDark ? 'bg-blue-900/20 border border-blue-700/50 text-blue-300' : 'bg-blue-50 border border-blue-200 text-blue-700'
							}`}>
								<Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
								<span>Select a different contact and use the shared contact, location, and person lookup for this appointment.</span>
							</div>

							<UnifiedContactLookup
								contacts={sharedContacts.filter((contact) => contact.id !== (job.contact_id ?? -1))}
								locations={sharedOverrideLocations}
								contactPeople={sharedOverridePeople}
								selectedContactId={overrideContact?.id ?? null}
								selectedLocationId={overrideLocationId}
								selectedContactPersonId={selectedContactPersonId}
								selectedContactPersonName={resolvedDifferentContactPerson ? buildName(resolvedDifferentContactPerson.name, resolvedDifferentContactPerson.first_name, resolvedDifferentContactPerson.last_name) : ''}
								onContactChange={handleSharedOverrideContactChange}
								onLocationChange={handleSharedOverrideLocationChange}
								onContactPersonChange={handleSharedOverrideContactPersonChange}
								onUnifiedSelect={handleSharedOverrideUnifiedSelect}
								onLocationCreated={handleSharedOverrideLocationCreated}
								onPersonCreated={handleSharedOverridePersonCreated}
								loadingContacts={loadingContacts}
								loadingLocations={loadingOverrideLocations}
								loadingPeople={loadingOverrideContactPeople}
								isDark={isDark}
								contactLabel="Appointment Contact *"
								locationLabel="Appointment Location *"
								personLabel="Appointment Contact Person"
							/>

							{overrideContact && overrideLocations.length === 0 && !loadingOverrideLocations ? (
								<div className={`rounded-lg border p-3 text-sm ${isDark ? 'border-amber-700/40 bg-amber-900/20 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
									The selected contact does not have saved locations yet. Use <span className="font-medium">New Location</span> above or switch to <span className="font-medium">Custom location</span> for an ad-hoc address.
								</div>
							) : null}
						</div>
					) : null}

					{locationSource === 'custom' ? (
						<div className="space-y-3">
							<div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
								isDark ? 'bg-orange-900/20 border border-orange-700/50 text-orange-300' : 'bg-orange-50 border border-orange-200 text-orange-700'
							}`}>
								<Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
								<span>This location is stored only on this appointment and will <strong>not</strong> be saved to the contact&apos;s locations.</span>
							</div>

							<LocationAutocomplete
								value={customLocationSearch}
								onChange={(value) => {
									setCustomLocationSearch(value);
									setError(null);
								}}
								onPlaceSelect={(location) => {
									setCustomLocation(location);
									setCustomLocationSearch(location.name);
									setError(null);
								}}
								onClear={() => {
									setCustomLocationSearch('');
									setCustomLocation(null);
									setError(null);
								}}
								isDark={isDark}
							/>

							{customLocation ? (
								<CustomLocationResult
									location={customLocation}
									onClear={() => {
										setCustomLocation(null);
										setCustomLocationSearch('');
										setError(null);
									}}
									isDark={isDark}
								/>
							) : (
								<p className={subtleTextClassName}>Start typing to search — select a result from the map search dropdown to capture coordinates.</p>
							)}

							<div>
								<label className={labelClassName}>Contact person at location</label>
								<input
									type="text"
									value={customContactPerson}
									onChange={(event) => {
										setCustomContactPerson(event.target.value);
										setError(null);
									}}
									className={fieldClassName}
									placeholder="e.g. John Smith — site manager"
								/>
							</div>
						</div>
					) : null}
				</div>

				{error ? (
					<div className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${isDark ? 'border-red-700/40 bg-red-900/20 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>
						<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
						<span>{error}</span>
					</div>
				) : null}

				<div className={`flex justify-end gap-3 border-t pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
					<button
						type="button"
						onClick={onClose}
						className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
						disabled={submitting}
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleConfirm}
						disabled={submitting || !canCreate}
						title={!canCreate ? 'You do not have permission to create appointments.' : undefined}
						className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-green-700 hover:to-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
						Create appointment
					</button>
				</div>
				</div>
			</DialogContent>
		</Dialog>

		</>
	);
};

export default AppointmentDropSetupModal;
